import { useState, useEffect } from "react";
import { LOCAL_STORAGE_KEYS } from "../lib/constants";

export interface CurrentMortgage {
  rate: string;
  loanProgram: string;
  monthlyPayment: string;
  calculatedMonthlyPaymentPI?: string;
  mortgageInsurance: string;
}

export interface RefiBuddyData {
  currentMortgage: CurrentMortgage;
  nationalAverageRate?: number;
  intent?: "refinance_now" | "get_notified";
  email?: string;
  timestamp?: string;
}

export type RefiBuddyView = "mortgage_details" | "rate_comparison";

export const useRefiBuddyState = () => {
  const [refiBuddyData, setRefiBuddyData] = useState<RefiBuddyData>({
    currentMortgage: {
      rate: "",
      loanProgram: "",
      monthlyPayment: "",
      mortgageInsurance: "",
    },
  });
  const [currentView, setCurrentView] =
    useState<RefiBuddyView>("mortgage_details");
  const [isLoading, setIsLoading] = useState(true);

  // Load data from localStorage on mount
  useEffect(() => {
    const loadData = () => {
      try {
        const savedData = localStorage.getItem(
          LOCAL_STORAGE_KEYS.REFI_BUDDY_DATA
        );
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          setRefiBuddyData(parsedData);
        }
      } catch (error) {
        console.error("Error loading RefiBuddy data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const saveRefiBuddyData = (data: Partial<RefiBuddyData>) => {
    const newData = { ...refiBuddyData, ...data };
    setRefiBuddyData(newData);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.REFI_BUDDY_DATA,
      JSON.stringify(newData)
    );
  };

  const setCurrentMortgage = (mortgage: Partial<CurrentMortgage>) => {
    const newMortgage = { ...refiBuddyData.currentMortgage, ...mortgage };
    saveRefiBuddyData({ currentMortgage: newMortgage });
  };

  const setRateComparison = (nationalAverageRate: number) => {
    saveRefiBuddyData({ nationalAverageRate });
  };

  const setUserIntent = (
    intent: "refinance_now" | "get_notified",
    email: string
  ) => {
    saveRefiBuddyData({
      intent,
      email,
      timestamp: new Date().toISOString(),
    });
  };

  const setEmail = (email: string) => {
    saveRefiBuddyData({ email });
  };

  const clearRefiBuddyData = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.REFI_BUDDY_DATA);
    setRefiBuddyData({
      currentMortgage: {
        rate: "",
        loanProgram: "",
        monthlyPayment: "",
        mortgageInsurance: "",
      },
    });
  };

  return {
    refiBuddyData,
    currentView,
    setCurrentView,
    isLoading,
    setCurrentMortgage,
    setRateComparison,
    setUserIntent,
    setEmail,
    clearRefiBuddyData,
  };
};
